async function getCart(){
    const callCart = await fetch("/cart.js", {method :"GET", credentials: 'same-origin'});
    return callCart.json();
}

/**
 * Enhances cart data with compare_at_price information to enable strikethrough pricing
 * 
 * The Shopify cart API (/cart.js) doesn't include compare_at_price data, so we need to:
 * 1. Fetch each product's data individually to get compare_at_price
 * 2. Calculate the original line price for discounted items
 * 3. Calculate the total original price for the entire cart
 * 
 * @param {Object} cart - Cart data from Shopify cart API
 * @returns {Promise<Object>} Enhanced cart data with original_line_price and original_total_price
 */
async function enhanceCartWithComparePrices(cart) {
    let originalTotalPrice = 0;
    
    // Process each item in the cart
    for (let item of cart.items) {
        try {
            // Fetch product data to get compare_at_price information
            const productResponse = await fetch(`/products/${item.handle}.js`);
            const product = await productResponse.json();
            
            // Find the specific variant that matches this cart item
            const variant = product.variants.find(v => v.id === item.variant_id);
            
            if (variant) {
                // Check if this variant has a compare_at_price (discount)
                if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                    // Calculate original line price: compare_at_price × quantity
                    const newOriginalLinePrice = variant.compare_at_price * item.quantity;
                    item.original_line_price = newOriginalLinePrice;
                    originalTotalPrice += newOriginalLinePrice;
                } else {
                    // No discount, use the final line price
                    originalTotalPrice += item.final_line_price;
                }
            } else {
                // Variant not found, fallback to final line price
                originalTotalPrice += item.final_line_price;
            }
        } catch (e) {
            // Error fetching product data, fallback to final line price
            originalTotalPrice += item.final_line_price;
        }
    }
    
    // Add the calculated original total price to the cart object
    cart.original_total_price = originalTotalPrice;
    
    return cart;
}

/**
 * Adds a product to the cart and opens the cart drawer with enhanced pricing data
 * @param {Object} dataPrd - Product data object containing id, form_type, and quantity
 */
async function addToCartLight(dataPrd){
    // Add product to cart via Shopify's cart API
    const callAdd = await window.fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
        },
        body: JSON.stringify(dataPrd)
    }); 
    const rep = callAdd.json(); 
    
    // Get updated cart data and enhance it with compare_at_price information
    const cart = await getCart();
    const enhancedCart = await enhanceCartWithComparePrices(cart);
    
    // Open cart drawer and update it with enhanced cart data
    wetheme.toggleRightDrawer('cart', true, {
        cart: enhancedCart
    }); 
    wetheme.updateCartDrawer(enhancedCart); 
    clearLoading();
}

/**
 * Attaches click event listeners to all add-to-cart buttons
 * This ensures that when new buttons are added dynamically, they get the proper event handlers
 */
function addToCart() {
    document.querySelectorAll(".add-to-cart").forEach(btnAdd => {
        btnAdd.removeEventListener("click", handleAddToCart);
        btnAdd.addEventListener("click", handleAddToCart);
    });
}

/**
 * Handles the click event on add-to-cart buttons
 * Extracts product ID and calls addToCartLight with the product data
 */
function handleAddToCart() {
    addLoading(this);
    var idPrd = this.dataset.id;
    var dataPrd = {
        "id": idPrd,
        "form_type": "product",
        "quantity": 1
    };
    addToCartLight(dataPrd);
}

// Initialize add-to-cart functionality on page load
addToCart();

// Re-initialize when cart drawer is opened or refreshed (for dynamically added buttons)
document.addEventListener('cartDrawerOpened', addToCart);
document.addEventListener('cartDrawerRefreshed', addToCart);

/**
 * Adds loading state to a button/element
 * @param {HTMLElement} target - The element to add loading state to
 */
function addLoading(target){
    target.classList.add("loading");
}

/**
 * Removes loading state from all elements with the "loading" class
 */
function clearLoading(){
    document.querySelectorAll(".loading").forEach(lo => {
        lo.classList.remove("loading");
    });
}

/**
 * Override the theme's updateCartDrawer function to enhance cart data with compare_at_price information
 * 
 * This is the key integration point that ensures all cart updates (quantity changes, 
 * item removals, etc.) include the enhanced pricing data for strikethrough display.
 * 
 * We wait for DOM to be ready to ensure the theme is fully initialized before overriding.
 */
document.addEventListener('DOMContentLoaded', function() {
    if (window.wetheme && window.wetheme.updateCartDrawer) {
        // Store reference to the original function
        const originalUpdateCartDrawer = window.wetheme.updateCartDrawer;
        
        // Override with our enhanced version
        window.wetheme.updateCartDrawer = async function(cart) {
            // Enhance cart with compare_at_price data before passing to original function
            const enhancedCart = await enhanceCartWithComparePrices(cart);
            
            // Call the original function with enhanced cart data
            return originalUpdateCartDrawer.call(this, enhancedCart);
        };
    }
});